import { useState, type ReactNode } from "react";
import {
  Pressable,
  Text,
  View,
  ScrollView,
  type ViewStyle,
} from "react-native";
import { Check, ChevronDown, type LucideIcon } from "lucide-react-native";
import {
  colors,
  radii,
  semanticColors,
  spacing,
  typography,
  withAlpha,
} from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import { Dialog } from "./Dialog";
import { DialogHeader } from "./DialogHeader";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Modal-based single-select. Replaces the "Pressable showing the selected
 * label, taps open a custom dropdown / sheet" pattern audit task 2.2
 * flagged as missing — every modal that needs a pick rolls its own.
 *
 * Generic over the option's `value` type. The selected option is matched
 * by value equality (===), so primitive values work out of the box and
 * complex objects need a stable identity.
 *
 * On press the field opens a centered Dialog with the options list. Tap an
 * option → onChange + dismiss. Tap the ✕ or the backdrop → dismiss without
 * change.
 *
 * State coverage via MoonFieldInput + MoonFormField composition (idle /
 * focused / error / disabled / loading). Focused mirrors the Dialog open
 * state so the field's border picks up the warm accent while the sheet is
 * on screen.
 */
type Option<T> = {
  label: string;
  value: T;
  /** Optional one-line description shown beneath the label in the picker. */
  description?: string;
};

type Props<T> = {
  /** Optional for compact repeaters whose card title already gives context. */
  label?: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  options: Option<T>[];
  value: T | null;
  onChange: (next: T) => void;
  placeholder?: string;
  /** Title shown in the picker dialog. Defaults to `label`. */
  pickerTitle?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  testID?: string;
  startVisual?: ReactNode;
  /** Stable analytics identifier for the question these options answer. */
  action?: string;
};

export function MoonSelectField<T>({
  label,
  labelIcon,
  options,
  value,
  onChange,
  placeholder = "Select…",
  pickerTitle,
  error,
  hint,
  required,
  disabled,
  loading,
  style,
  testID,
  startVisual,
  action,
}: Props<T>) {
  const fontSize = useInputFontSize();
  const capturePress = usePressCapture("MoonSelectField");
  const [open, setOpen] = useState(false);

  const selected = options.find((o) => o.value === value) ?? null;

  const field = (
    <MoonFieldInput hasError={!!error} focused={open} paddingY={12}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          selected
            ? `${label ?? placeholder}: ${selected.label}`
            : `Select ${label ?? placeholder}`
        }
        disabled={disabled}
        onPress={() => {
          capturePress(
            pressLabel(selected?.label, label, placeholder),
            action ?? "open_select_field",
          );
          setOpen(true);
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        {startVisual ? <View>{startVisual}</View> : null}
        <Text
          style={{
            flex: 1,
            fontFamily: typography.families.sans,
            fontSize,
            color: selected ? colors.text : colors.textFaint,
          }}
        >
          {selected ? selected.label : placeholder}
        </Text>
        <ChevronDown size={16} color={colors.textFaint} strokeWidth={1.5} />
      </Pressable>
    </MoonFieldInput>
  );

  return (
    <>
      {label ? (
        <MoonFormField
          label={label}
          labelIcon={labelIcon}
          error={error}
          hint={hint}
          required={required}
          disabled={disabled}
          loading={loading}
          testID={testID}
          style={style}
        >
          {field}
        </MoonFormField>
      ) : (
        field
      )}

      <Dialog
        visible={open}
        onClose={() => setOpen(false)}
        layout="centered"
        closeButton="minimal"
        closeAction={action ? `${action}_close` : "close_select_field"}
        dismissOnBackdropTap
      >
        <DialogHeader
          title={pickerTitle ?? label}
          style={{ marginBottom: spacing.lg }}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: 360 }}
        >
          <View style={{ gap: 6 }}>
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <Pressable
                  key={String(opt.value)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  onPress={() => {
                    capturePress(
                      pressLabel(opt.label),
                      action ?? "select_field_option",
                    );
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: radii.md,
                    backgroundColor: isSelected
                      ? semanticColors.accent.tint
                      : "transparent",
                    borderWidth: 1,
                    borderColor: isSelected
                      ? withAlpha(semanticColors.accent.default, 0.13)
                      : "transparent",
                    gap: 12,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontFamily: typography.families.sansMedium,
                        fontSize: 16,
                        fontWeight: isSelected ? "600" : "400",
                        color: colors.text,
                      }}
                    >
                      {opt.label}
                    </Text>
                    {opt.description ? (
                      <Text
                        style={{
                          fontFamily: typography.families.sans,
                          fontSize: 12,
                          color: colors.textMid,
                          marginTop: 2,
                        }}
                      >
                        {opt.description}
                      </Text>
                    ) : null}
                  </View>
                  {isSelected ? (
                    <Check
                      size={16}
                      color={semanticColors.accent.default}
                      strokeWidth={2}
                    />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </ScrollView>
      </Dialog>
    </>
  );
}
